import React, { Component, Fragment } from "react";
import { dateToArray, arrayToDate } from "../../formHelpers";
import { FormattedMessage } from "react-intl";
import { getPhone } from "../../../../domain/profile/utils";
import accountTypeOptions from "./accountTypeOptions";
import moment from "moment";
import mapBackendErrors from "assets/scripts/utils/mapBackendErrors";
import { getAmountForRouteGroup, allowIndividualAccountPayment, trimBankInfo } from "./utils";
import Disclaimer from "assets/scripts/components/forms/secure/PayNowForm/Disclaimer/Disclaimer";
import { _numbersOnly } from "assets/scripts/domain/profile/dataConversion";
import PaymentInfoForm from "./PaymentInfoForm/PaymentInfoForm";
import BankInfoForm from "./BankInfoForm/BankInfoForm";
import PersonalInfoForm from "./PersonalInfoForm/PersonalInfoForm";
import styles from "./PayNowForm.module.less";
import ConfirmPayment from "assets/scripts/components/containers/secure/ConfirmPayment/ConfirmPayment";
import FullPaymentForm from "./FullPaymentForm/FullPaymentForm";
import tryParseNumber from "assets/scripts/utils/tryParseNumber";
import keyBy from "lodash/keyBy";
import MDPatientSheet from "assets/scripts/components/common/MDPatientSheet/MDPatientSheet";
export default class PayNowForm extends Component {
    constructor(props, context) {
        super(props, context);
        const { group, profile } = props;
        const { bankAccounts } = profile;
        const accountTypeKeys = Object.keys(accountTypeOptions);
        const bankInfoId = !process.env.FEATURE_SAVE_BANK_INFO || bankAccounts.length === 0
            ? "-1"
            : bankAccounts[0].id.toString();
        const amountsOrder = [];
        const amounts = {};
        // preselect all accounts for a multi-account payment if 4 or less accounts
        const eligibleAccounts = group.accounts.filter(it => it.paymentEligible);
        if (eligibleAccounts.length <= 4) {
            for (const account of eligibleAccounts) {
                amountsOrder.push(account.id);
                amounts[account.id] = account.amountDue.toFixed(2);
            }
        }
        let amount;
        let date;
        const { settlementOffer, nextPayment } = props;
        if (settlementOffer != null) {
            amount = (settlementOffer.percentage * group.primary.balance).toFixed(2);
            date = dateToArray(null);
        }
        else if (nextPayment != null) {
            amount = nextPayment.amount.toFixed(2);
            date = dateToArray(group.primary.allowScheduledPayment ? moment(nextPayment.date) : null);
        }
        else {
            amount = getAmountForRouteGroup(group).amountDue.toFixed(2);
            date = dateToArray(group.primary.allowScheduledPayment ? moment().startOf("day") : null);
        }
        this.state = {
            value: {
                amount,
                date,
                bankInfoId,
                routingNumber: "",
                accountNumber: "",
                accountType: accountTypeKeys.length === 1 ? accountTypeKeys[0] : "",
                saveBankInfo: false,
                bankInfoName: "",
                distribute: true,
                amountsOrder,
                amounts,
                ...this._getPersonalInfo(bankInfoId)
            }
        };
        if (process.env.NODE_ENV === "development") {
            // test values for development
            Object.assign(this.state.value, {
                routingNumber: "112200439",
                accountNumber: "12345678",
                accountType: "c"
            });
        }
        this._handleValueChange = this._handleValueChange.bind(this);
    }
    render() {
        const { props, state } = this;
        const now = moment();
        const { globalError, backendErrors } = mapBackendErrors(props.fieldErrors);
        const formProps = {
            group: props.group,
            profile: props.profile,
            value: state.value,
            onChange: this._handleValueChange,
            backendErrors,
            isLoading: props.isSaving,
            now,
            globalError,
            settlementOffer: props.settlementOffer
        };
        if (props.step === "confirm") {
            return (<ConfirmPayment group={props.group} payment={this._getPayment()} onBack={() => props.onStepBack(process.env.FEATURE_PAYMENT_WIZARD ? "personal" : "payment")} onConfirm={props.onCreatePayment} error={props.error} isSaving={props.isSaving}/>);
        }
        return (<Fragment>
				<div className={styles.form}>
					{props.step === "payment" && (<Fragment>
							{process.env.FEATURE_PAYMENT_WIZARD && (<PaymentInfoForm {...formProps} validateAmount={props.validateAmount} hideDate={props.hideDate} onContinue={() => props.onStepForward("banking")}/>)}

							{!process.env.FEATURE_PAYMENT_WIZARD && (<FullPaymentForm {...formProps} onContinue={() => props.onStepForward("confirm")}/>)}

							{props.group.primary.hasMDPatientInfoSheet && (<MDPatientSheet clientId={props.group.primary.clientId} clientName={props.group.primary.clientName} hasSpanish={props.group.primary.hasMDPatientInfoSheetES} padTop/>)}
						</Fragment>)}

					{props.step === "banking" && (<BankInfoForm {...formProps} disableBankSave={props.disableBankSave || false} onBack={() => props.onStepBack("payment")} onContinue={() => props.onStepForward("personal")}/>)}

					{props.step === "personal" && (<PersonalInfoForm {...formProps} hideEmail={this.props.hideEmail} onBack={() => this.props.onStepBack("banking")} onContinue={() => this.props.onStepForward("confirm")}/>)}
				</div>

				<Disclaimer value={state.value} now={now}>
					{(!props.group.primary.specialtyLive || props.step === "banking") && (<FormattedMessage tagName="p" id="paynow.acknowledge" defaultMessage={"I authorize DCM Services, LLC to process this payment as an electronic funds "
            + "transfer from this bank account. If this payment is from a business/corporate "
            + "account, I make these authorizations as an authorized business/corporate "
            + "representative. Funds may be withdrawn from the bank account as soon as the same "
            + "day DCM Services receives payment."}/>)}
				</Disclaimer>
			</Fragment>);
    }
    _handleValueChange(value) {
        this.setState(prevState => {
            let newValue = {
                ...prevState.value,
                ...value
            };
            if (newValue.bankInfoId !== prevState.value.bankInfoId) {
                newValue = {
                    ...newValue,
                    ...this._getPersonalInfo(newValue.bankInfoId),
                    saveBankInfo: false,
                };
            }
            return { value: newValue };
        });
    }
    _getBankInfo(bankInfoId) {
        if (bankInfoId === "-1" || this.props.profile === null || this.props.profile === undefined) {
            return null;
        }
        const id = parseInt(bankInfoId, 10);
        return this.props.profile.bankAccounts.find(it => it.id === id) || null;
    }
    _getPersonalInfo(bankInfoId) {
        const { profile } = this.props;
        const profileInfo = {
            ...profile,
            phone: getPhone(profile) || ""
        };
        const { firstName, lastName, address, address2, city, state, zip, phone, email } = this._getBankInfo(bankInfoId) || profileInfo;
        return {
            firstName,
            lastName,
            address,
            address2,
            city,
            state,
            zip,
            phone: _numbersOnly(phone),
            email
        };
    }
    _getPayment() {
        const { group, settlementOffer } = this.props;
        const { value } = this.state;
        let { routingNumber, accountNumber, accountType } = trimBankInfo(value);
        let bankInfo = this._getBankInfo(value.bankInfoId);
        if (bankInfo != null) {
            routingNumber = accountNumber = accountType = "";
        }
        else if (value.saveBankInfo) {
            bankInfo = {
                id: 0,
                name: value.bankInfoName.trim(),
                address: "",
                address2: "",
                city: "",
                email: "",
                firstName: "",
                lastName: "",
                phone: "",
                state: "",
                zip: ""
            };
        }
        const date = arrayToDate(value.date);
        let amounts;
        if (this.props.validateAmount) {
            amounts = [{
                    payableId: group.primary.id,
                    amount: tryParseNumber(value.amount) || 0,
                    accountBalance: 0
                }];
        }
        else if (value.distribute || !allowIndividualAccountPayment(group)) {
            amounts = this._getDistributedAmounts();
        }
        else {
            const accounts = keyBy(group.accounts, it => it.id);
            amounts = Object.keys(value.amounts)
                .map(name => {
                const payableId = parseInt(name, 10);
                return {
                    payableId,
                    amount: tryParseNumber(value.amounts[payableId]) || 0,
                    accountBalance: accounts[payableId].balance
                };
            })
                .filter(it => it.amount > 0);
        }
        return {
            payableId: group.primary.id,
            firstName: value.firstName,
            lastName: value.lastName,
            address: value.address,
            address2: value.address2,
            city: value.city,
            stateProvince: value.state,
            postalCode: value.zip,
            phone: value.phone,
            emailAddress: value.email,
            routingNumber,
            accountNumber,
            accountType,
            bankInfo,
            scheduledTimestampUtc: date == null ? null : date.toISOString(),
            agreed: null,
            amounts,
            settlementOfferId: settlementOffer == null ? null : settlementOffer.id
        };
    }
    _getDistributedAmounts() {
        // to avoid floating point issues all calculaions are done using cents
        const total = parseFloat(this.state.value.amount);
        const { accounts } = this.props.group;
        const paymentAmount = toCents(total);
        const openAccounts = [];
        let totalAmountDue = 0;
        const amounts = {};
        const balances = {};
        for (const account of accounts) {
            amounts[account.id] = 0;
            balances[account.id] = account.balance;
            if (account.paymentEligible) {
                totalAmountDue += toCents(account.maxPayment);
                openAccounts.push(account);
            }
        }
        let distributedAmount = 0;
        for (const account of openAccounts) {
            const amountDue = toCents(account.maxPayment);
            const ratio = totalAmountDue === 0
                ? 1 / accounts.length
                : amountDue / totalAmountDue;
            const amount = amounts[account.id] = Math.min(Math.round(ratio * paymentAmount), amountDue);
            distributedAmount += amount;
        }
        // cycle through accounts to distributing any lost or added pennies
        let diff = paymentAmount - distributedAmount;
        const sign = diff / Math.abs(diff);
        let index = 0;
        while (diff !== 0) {
            const account = openAccounts[index];
            amounts[account.id] += sign;
            index = (index + 1) % openAccounts.length;
            diff -= sign;
        }
        return openAccounts
            .map(({ id }) => {
            return {
                payableId: id,
                amount: amounts[id] / 100,
                accountBalance: balances[id]
            };
        })
            .filter(it => it.amount !== 0);
    }
}
function toCents(amount) {
    return Math.round(100 * amount);
}



// WEBPACK FOOTER //
// ./src/assets/scripts/components/forms/secure/PayNowForm/PayNowForm.tsx